import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Translate, I18n } from 'react-redux-i18n';
import { toggleErrorDialog } from 'actions';
import { Label, Input as LabelInput } from 'reactstrap';
import Form from 'components/Form';
import Button from 'components/Form/Button';
import Input from 'components/Form/Input';
import { TYPE_TEXT } from 'components/Form/Input/input-type';
import ButtonGroup from 'components/ButtonGroup';
import FormGroup from 'components/Form/FormGroup';
import PropTypes from 'prop-types';
import ModalWrapper from 'components/ModalWrapper';
import Modal from 'components/Modal';
import serialize from 'form-serialize';
import AuthFeature from 'components/AuthFeature';
import { AUTH_DELETE_CUSTOMER_INFO } from 'constants/permission';
import { useDeleteCustomerMutation } from 'redux/hook/useCustomer';
import { useHistory } from 'react-router-dom';
import { CUSTOMER_FINDER } from 'constants/routes';

import './delete-customer.scss';


const DeleteCustomer = ({
    isAssociated,
    customerId,
    allowDeleteCustomer,
    customerPhone,
    btnText,
}) => {
    const dispatch = useDispatch();
    const [deleteCustomer] = useDeleteCustomerMutation();
    const i18n = useSelector(state => state.i18n);
    const [isShowModal, setIsShowModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const formRef = useRef();
    const elPhone = useRef();
    const elCheckUser = useRef();
    const history = useHistory();

    const handleClose = () => {
        setIsShowModal(false);
        setIsSubmitting(false);
        setErrorMessage();
    };

    const handleFormSubmit = async () => {
        const formData = serialize(formRef.current, { hash: true });
        const { phone } = formData;
        setIsSubmitting(true);
        if (customerPhone !== phone) {
            setErrorMessage(I18n.t('customer.phone_mismatch_error'));
            setIsSubmitting(false);
            return;
        }
        else {
            setErrorMessage('');
            const { error: deleteError } = await deleteCustomer({
                id: customerId,
                phone,
                check_gogoro_user: elCheckUser.current?.checked
            });
            if (deleteError) {
                const { code } = deleteError?.data || {};
                const message = code !== undefined ?
                    I18n.t(`customer.delete_customer_error_${ code }`) :
                    I18n.t('general_error_message');
                dispatch(toggleErrorDialog(message));
                handleClose();
                return;
            }
            history.push(CUSTOMER_FINDER)
        }
    };

    useEffect(() => { }, [i18n]);
    return (
        <AuthFeature requiredList={ [AUTH_DELETE_CUSTOMER_INFO] } >
            <>
                {
                    allowDeleteCustomer ? (
                        <Button
                            className="customer-card-info-btn"
                            type="button"
                            outline
                            onClick={ () => setIsShowModal(true) }
                        >
                            <Translate value={ btnText } />
                        </Button>
                    ) : null
                }
            </>
            <>
                {
                    isShowModal ? (
                        <ModalWrapper>
                            <Modal
                                className="delete-customer-modal"
                                title={ I18n.t('customer.delete_customer') }
                                disabledCloseOnBg
                                onClose={ handleClose }
                            >
                                <AuthFeature requiredList={ [AUTH_DELETE_CUSTOMER_INFO] } >
                                    <Form
                                        className="delete-customer-form"
                                        innerRef={ formRef }
                                        stopSubmitByEnter
                                        inProgress={ isSubmitting }
                                    >
                                        <div className="message">{ I18n.t('customer.delete_customer_hint') }</div>
                                        <FormGroup title="">
                                            <Input
                                                name="phone"
                                                type={ TYPE_TEXT }
                                                value={ elPhone.current?.value || '' }
                                                innerRef={ elPhone }
                                                caption="customer.mobile"
                                                required
                                            />
                                            <p className="error-message">{ errorMessage }</p>
                                            {
                                                isAssociated && (
                                                    <Label check>
                                                        <LabelInput
                                                            name="check_gogoro_user"
                                                            innerRef={ elCheckUser }
                                                            type="checkbox"
                                                        />
                                                        <Translate
                                                            value="customer.check_gogoro_user"
                                                        />
                                                    </Label>
                                                )
                                            }
                                        </FormGroup>
                                        <ButtonGroup>
                                            <Button type="button" className="btn-cancel" outline onClick={ handleClose }>
                                                <Translate value="cancel" />
                                            </Button>
                                            <Button
                                                color="primary"
                                                type="button"
                                                disabled={ isSubmitting }
                                                onClick={ handleFormSubmit }
                                            >
                                                <Translate value="submit" />
                                            </Button>
                                        </ButtonGroup>
                                    </Form>
                                </AuthFeature>
                            </Modal>
                        </ModalWrapper>
                    ) : null
                }
            </>
        </AuthFeature>
    );
};

DeleteCustomer.propTypes = {
    isAssociated: PropTypes.bool,
    customerId: PropTypes.string,
    customerPhone: PropTypes.string,
    allowDeleteCustomer: PropTypes.bool,
    btnText: PropTypes.string,
    onSuccess: PropTypes.func,
};
DeleteCustomer.defaultProps = {
    isAssociated: false,
    customerId: undefined,
    customerPhone: undefined,
    allowDeleteCustomer: false,
    btnText: 'customer.delete_customer',
    onSuccess: () => {},
};

export default DeleteCustomer;
